import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Icon from './Icon';

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  if (diff < 172800) return 'Yesterday';
  return new Date(dateStr).toLocaleDateString();
}

export default function HeaderIcons() {
  const { trainer, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentMessages, setRecentMessages] = useState([]);
  const settingsRef = useRef(null);
  const notifRef = useRef(null);

  const isDev = trainer?.role === 'dev';

  // Poll unread count every 60s
  useEffect(() => {
    if (!trainer) return;
    function fetchCount() {
      api.get('/messages/unread-count').then(d => setUnreadCount(d.count)).catch(() => {});
    }
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [trainer]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, []);

  function openNotif() {
    if (!notifOpen) {
      api.get('/messages/inbox?limit=5').then(msgs => setRecentMessages(msgs)).catch(() => {});
    }
    setNotifOpen(v => !v);
    setSettingsOpen(false);
  }

  function openSettings() {
    setSettingsOpen(v => !v);
    setNotifOpen(false);
  }

  function handleMsgClick(msg) {
    api.put('/messages/' + msg.id + '/read').catch(() => {});
    if (!msg.is_read) setUnreadCount(c => Math.max(0, c - 1));
    setRecentMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
    setNotifOpen(false);
    navigate('/messages');
  }

  function handleLogout() {
    setSettingsOpen(false);
    logout();
    navigate('/login');
  }

  if (!trainer) return null;

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, zIndex: 200, display: 'flex', alignItems: 'flex-start', paddingTop: 'calc(env(safe-area-inset-top) + 6px)', paddingRight: 4 }}>

      {/* Notification icon */}
      <div ref={notifRef} style={{ position: 'relative' }}>
        <button onClick={openNotif} className="icon-btn" title="Messages">
          <Icon name="icon-notification.png" size={24} alt="Notifications" />
          {unreadCount > 0 && (
            <span style={{ position: 'absolute', top: 4, right: 4, background: 'var(--color-primary)', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {notifOpen && (
          <div style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top) + 56px)', right: 56, width: 300, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 300, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>Messages</span>
              {unreadCount > 0 && (
                <button onClick={() => { api.put('/messages/read-all').catch(() => {}); setUnreadCount(0); setRecentMessages(prev => prev.map(m => ({ ...m, is_read: true }))); }}
                  style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                  Mark all read
                </button>
              )}
            </div>
            {recentMessages.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>No messages</div>
            ) : (
              recentMessages.map(msg => (
                <button key={msg.id} onClick={() => handleMsgClick(msg)}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', width: '100%', background: msg.is_read ? 'transparent' : 'rgba(215,38,56,0.04)', border: 'none', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', textAlign: 'left' }}>
                  {!msg.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)', flexShrink: 0, marginTop: 4 }} />}
                  <div style={{ flex: 1, minWidth: 0, marginLeft: msg.is_read ? 18 : 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{msg.sender_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {msg.subject || msg.body.substring(0, 60)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{timeAgo(msg.created_at)}</div>
                  </div>
                </button>
              ))
            )}
            <button onClick={() => { setNotifOpen(false); navigate('/messages'); }}
              style={{ display: 'block', width: '100%', padding: '12px', background: 'none', border: 'none', borderTop: '1px solid var(--color-border)', color: 'var(--color-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>
              View all messages
            </button>
          </div>
        )}
      </div>

      {/* Settings icon */}
      <div ref={settingsRef} style={{ position: 'relative' }}>
        <button onClick={openSettings} className="icon-btn" title="Settings">
          <Icon name="icon-settings2.png" size={24} alt="Settings" />
          {isDev && (
            <span style={{ position: 'absolute', top: 2, right: 2, background: '#D72638', borderRadius: 3, width: 8, height: 8 }} />
          )}
        </button>

        {settingsOpen && (
          <div style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top) + 56px)', right: 8, width: 220, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 300, overflow: 'hidden' }}>
            <DropdownItem icon="icon-settings.png" label="Account Settings" onClick={() => { setSettingsOpen(false); navigate('/settings/account'); }} />
            <DropdownItem icon="icon-settings.png" label="Branding & Logo"  onClick={() => { setSettingsOpen(false); navigate('/settings/branding'); }} />
            <DropdownItem icon="icon-settings.png" label="Landing Page"     onClick={() => { setSettingsOpen(false); navigate('/settings/landing-page'); }} />
            {isAdmin && <DropdownItem icon="icon-add.png"  label="Invite Team Members" onClick={() => { setSettingsOpen(false); navigate('/settings/invite'); }} />}
            {isAdmin && <DropdownItem icon="icon-user.png" label="Manage Team"          onClick={() => { setSettingsOpen(false); navigate('/settings/team'); }} />}
            {isDev && (
              <div style={{ borderTop: '1px solid var(--color-border)' }}>
                <DropdownItem icon="icon-settings.png" label="🛠 Database Inspector" onClick={() => { setSettingsOpen(false); navigate('/dev/tools'); }} dev />
              </div>
            )}
            <div style={{ borderTop: '1px solid var(--color-border)' }}>
              <DropdownItem icon="icon-settings.png" label="Privacy Policy" onClick={() => { setSettingsOpen(false); navigate('/privacy-policy'); }} />
            </div>
            <div style={{ borderTop: '1px solid var(--color-border)' }}>
              <DropdownItem icon="icon-logout.png" label="Log Out" onClick={handleLogout} danger />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DropdownItem({ icon, label, onClick, danger, dev }) {
  return (
    <button onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500, color: danger ? 'var(--color-primary)' : dev ? '#7EC8E3' : 'var(--color-text)', textAlign: 'left' }}>
      <Icon name={icon} size={18} style={{ opacity: danger ? 1 : 0.7 }} />
      {label}
    </button>
  );
}
